import React from 'react';

import { describeEmail, getDatasetLabel, getEmailChanges } from 'utils/adminEmailUtils';
import DeliveryBadge from './DeliveryBadge';
import EmailStatusBadge from './EmailStatusBadge';

const EmailDetailsModal = ({ email, onClose, onRetry }) => {
	const changes = getEmailChanges(email.change_data);

	// Format date
	const formatDate = (dateString) => {
		if (!dateString) return '-';
		try {
			return dateString;
		} catch {
			return dateString;
		}
	};

	// The SMS status badge; the email's own status uses EmailStatusBadge, as the table does
	const getStatusBadge = (status) => {
		const badges = {
			pending: 'bg-warning',
			sent: 'bg-success',
			sent_digest: 'bg-info',
			failed: 'bg-danger',
		};

		return (
			<span className={`badge ${badges[status] || 'bg-secondary'}`}>
				{status}
			</span>
		);
	};

	const smsMissingReasonLabel = (reason) => {
		const labels = {
			not_alert_type: 'This email type does not send SMS (alerts only).',
			dataset_unsupported: 'This dataset is not mapped to an SMS template.',
			user_not_opted_in: 'User has not opted in to SMS alerts.',
			no_phone: 'User has no phone number on file.',
			not_queued: 'No SMS was queued for this alert.',
		};

		return labels[reason] || 'No matching SMS.';
	};

	return (
		<>
			{/* Modal Backdrop */}
			<div
				className="modal-backdrop fade show"
				onClick={onClose}
				style={{ zIndex: 1040 }}
			/>

			{/* Modal */}
			<div
				className="modal fade show d-block"
				tabIndex="-1"
				style={{ zIndex: 1050 }}
			>
				<div className="modal-dialog modal-lg modal-dialog-scrollable">
					<div className="modal-content">
						{/* Header */}
						<div className="modal-header">
							<h5 className="modal-title">
								<i className="bi bi-envelope-open me-2" />
								Email Details
							</h5>
							<button type="button" className="btn-close" onClick={onClose} />
						</div>

						{/* Body */}
						<div className="modal-body">
							{/* Status Section */}
							<div className="mb-4">
								<h6 className="border-bottom pb-2 mb-3">Status Information</h6>
								<div className="row g-3">
									<div className="col-md-6" data-testid="email-details-status">
										<label className="text-muted small">Status</label>
										<div>
											<EmailStatusBadge email={email} showReason />
										</div>
									</div>
									<div className="col-md-6" data-testid="email-details-delivery">
										<label className="text-muted small">Delivery (Amazon SES)</label>
										<div>
											<DeliveryBadge status={email.delivery_status} />
											{email.delivery_status_at && (
												<small className="text-muted ms-2">
													{formatDate(email.delivery_status_at)}
												</small>
											)}
										</div>
									</div>
									<div className="col-md-6">
										<label className="text-muted small">Type</label>
										<div>
											<span className="badge bg-info">{email.type}</span>
										</div>
									</div>
									<div className="col-md-6">
										<label className="text-muted small">Attempts</label>
										<div>
											<span
												className={`badge ${
													email.attempts > 2 ? 'bg-danger' : 'bg-warning'
												}`}
											>
												{email.attempts}
											</span>
										</div>
									</div>
									<div className="col-md-6">
										<label className="text-muted small">Created At</label>
										<div>{formatDate(email.created_at)}</div>
									</div>
									{email.sent_at && (
										<div className="col-md-6">
											<label className="text-muted small">Sent At</label>
											<div className="text-success">
												<i className="bi bi-check-circle me-1" />
												{formatDate(email.sent_at)}
											</div>
										</div>
									)}
									{email.failed_at && (
										<div className="col-md-6">
											<label className="text-muted small">Failed At</label>
											<div className="text-danger">
												<i className="bi bi-x-circle me-1" />
												{formatDate(email.failed_at)}
											</div>
										</div>
									)}
								</div>
							</div>

							{/* Recipient Section */}
							<div className="mb-4">
								<h6 className="border-bottom pb-2 mb-3">
									Recipient Information
								</h6>
								<div className="row g-3">
									<div className="col-md-12">
										<label className="text-muted small">Email Address</label>
										<div className="d-flex align-items-center">
											<i className="bi bi-envelope me-2" />
											<strong>{email.to_email}</strong>
										</div>
									</div>
									{email.user && (
										<>
											<div className="col-md-6">
												<label className="text-muted small">User Name</label>
												<div>{email.user.name}</div>
											</div>
											<div className="col-md-6">
												<label className="text-muted small">User ID</label>
												<div>#{email.user.id}</div>
											</div>
										</>
									)}
								</div>
							</div>

							{/* Summary Section: what the email told the user, in plain language */}
							<div className="mb-4">
								<h6 className="border-bottom pb-2 mb-3">What this email told the user</h6>
								<p className="mb-2">{describeEmail(email)}</p>
								{email.class && (
									<div className="text-muted small mb-2">Dataset: {getDatasetLabel(email.class)}</div>
								)}
								{changes.length > 0 && (
									<table className="table table-sm table-bordered mb-0">
										<thead>
											<tr>
												<th>Field</th>
												<th>Was</th>
												<th>Now</th>
											</tr>
										</thead>
										<tbody>
											{changes.map((change) => (
												<tr key={change.field}>
													<td>{change.field}</td>
													<td className="text-muted">{change.from}</td>
													<td>{change.to}</td>
												</tr>
											))}
										</tbody>
									</table>
								)}
							</div>

							{/* Email Content Section */}
							<div className="mb-4">
								<h6 className="border-bottom pb-2 mb-3">Email Content</h6>
								<div className="row g-3">
									{email.subject && (
										<div className="col-md-12">
											<label className="text-muted small">Subject</label>
											<div className="p-2 bg-light rounded">
												{email.subject}
											</div>
										</div>
									)}
									{email.mailable_class && (
										<div className="col-md-12">
											<label className="text-muted small">Mailable Class</label>
											<div>
												<code className="text-primary">
													{email.mailable_class}
												</code>
											</div>
										</div>
									)}
									{email.class && (
										<div className="col-md-12">
											<label className="text-muted small">Dataset Class</label>
											<div>
												<code className="text-secondary">{email.class}</code>
											</div>
										</div>
									)}
								</div>
							</div>

							{/* Property Section */}
							{email.property && (
								<div className="mb-4">
									<h6 className="border-bottom pb-2 mb-3">Related Property</h6>
									<div className="row g-3">
										<div className="col-md-8">
											<label className="text-muted small">Address</label>
											<div>
												<i className="bi bi-building me-2" />
												{email.property.address}
											</div>
										</div>
										<div className="col-md-4">
											<label className="text-muted small">Property ID</label>
											<div>#{email.property.id}</div>
										</div>
										{email.item_id && (
											<div className="col-md-4">
												<label className="text-muted small">Item ID</label>
												<div>#{email.item_id}</div>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Error Section */}
							{email.error_message && (
								<div className="mb-4">
									<h6 className="border-bottom pb-2 mb-3 text-danger">
										<i className="bi bi-exclamation-triangle me-2" />
										Error Details
									</h6>
									<div className="alert alert-danger mb-0">
										<div className="mb-2">
											<strong>Error Message:</strong>
										</div>
										<div className="p-2 bg-white rounded">
											<code className="text-danger">{email.error_message}</code>
										</div>
										{email.error_code && (
											<div className="mt-2">
												<strong>Error Code:</strong>{' '}
												<span className="badge bg-danger">
													{email.error_code}
												</span>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Metadata Section */}
							{email.metadata && Object.keys(email.metadata).length > 0 && (
								<div className="mb-4">
									<h6 className="border-bottom pb-2 mb-3">Metadata</h6>
									<div className="p-3 bg-light rounded">
										<pre className="mb-0" style={{ fontSize: '0.85rem' }}>
											{JSON.stringify(email.metadata, null, 2)}
										</pre>
									</div>
								</div>
							)}

							{/* Diff Section */}
							{email.diff && (
								<div className="mb-4">
									<h6 className="border-bottom pb-2 mb-3">Changes (Diff)</h6>
									<div className="p-3 bg-light rounded">
										<pre className="mb-0" style={{ fontSize: '0.85rem' }}>
											{email.diff}
										</pre>
									</div>
								</div>
							)}

							{/* SMS Section */}
							<div className="mb-4">
								<h6 className="border-bottom pb-2 mb-3">
									<i className="bi bi-chat-dots me-2" />
									SMS
								</h6>
								{email.sms ? (
									<div className="row g-3">
										<div className="col-md-6">
											<label className="text-muted small">Status</label>
											<div>{getStatusBadge(email.sms.status)}</div>
										</div>
										<div className="col-md-6">
											<label className="text-muted small">Tier</label>
											<div>
												<span className="badge bg-info">
													{email.sms.tier}
												</span>
											</div>
										</div>
										<div className="col-md-6">
											<label className="text-muted small">Phone</label>
											<div>{email.sms.phone || '—'}</div>
										</div>
										<div className="col-md-6">
											<label className="text-muted small">Template</label>
											<div>
												<code>{email.sms.template_key}</code>
											</div>
										</div>
										{email.sms.sent_at && (
											<div className="col-md-6">
												<label className="text-muted small">Sent At</label>
												<div className="text-success">
													{formatDate(email.sms.sent_at)}
												</div>
											</div>
										)}
										{email.sms.twilio_sid && (
											<div className="col-md-6">
												<label className="text-muted small">Twilio SID</label>
												<div>
													<code>{email.sms.twilio_sid}</code>
												</div>
											</div>
										)}
										{email.sms.body && (
											<div className="col-md-12">
												<label className="text-muted small">Message Body</label>
												<div className="p-2 bg-light rounded">
													<pre
														className="mb-0"
														style={{
															fontSize: '0.85rem',
															whiteSpace: 'pre-wrap',
														}}
													>
														{email.sms.body}
													</pre>
												</div>
											</div>
										)}
										{email.sms.error_message && (
											<div className="col-md-12">
												<label className="text-muted small">Error</label>
												<div className="alert alert-danger mb-0">
													<code className="text-danger">
														{email.sms.error_message}
													</code>
												</div>
											</div>
										)}
									</div>
								) : (
									<div className="text-muted">
										{smsMissingReasonLabel(email.sms_missing_reason)}
									</div>
								)}
							</div>

							{/* Technical Details */}
							<div className="mb-0">
								<h6 className="border-bottom pb-2 mb-3">Technical Details</h6>
								<div className="row g-3">
									<div className="col-md-4">
										<label className="text-muted small">Email ID</label>
										<div>
											<code>#{email.id}</code>
										</div>
									</div>
									<div className="col-md-4">
										<label className="text-muted small">User ID</label>
										<div>
											<code>#{email.user_id}</code>
										</div>
									</div>
									<div className="col-md-4">
										<label className="text-muted small">Property ID</label>
										<div>
											<code>#{email.property_id}</code>
										</div>
									</div>
									<div className="col-md-12">
										<label className="text-muted small">Created At</label>
										<div>
											<small className="text-muted">{email.created_at}</small>
										</div>
									</div>
									<div className="col-md-12">
										<label className="text-muted small">Updated At</label>
										<div>
											<small className="text-muted">{email.updated_at}</small>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Footer */}
						<div className="modal-footer">
							<button
								type="button"
								className="btn btn-secondary"
								onClick={onClose}
							>
								Close
							</button>
							{email.status === 'failed' && onRetry && (
								<button
									type="button"
									className="btn btn-success"
									onClick={() => {
										onRetry(email.id);
										onClose();
									}}
								>
									<i className="bi bi-arrow-clockwise me-2" />
									Retry Email
								</button>
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default EmailDetailsModal;
